import React from 'react';
import { useTranslation } from 'next-i18next';
import ProfilePicture from '@components/molecules/profile/profile-picture';
import { NegotiationEvent } from '@interfaces/models/negotiation/negotiationEvent';
import { User } from '@interfaces/models/user';
import { Negotiation } from '@interfaces/models/negotiation/negotiation';
import { formatDateTime } from '@helpers/utils/date-time-formatter-helper';
import useUser from '@hooks/user/use-user';
import Link from '@components/atoms/link/link';
import Icon from '@components/atoms/icon/icon';
import PriceBreakDownTooltip from '../price-breakdown-tooltip/price-breakdown-tooltip';
import styles from './negotiation-history.module.scss';

type MMAOHistoryProps = {
  chatLink: string;
  events: Negotiation['events'];
  buyer: Negotiation['buyer'];
  seller: Negotiation['seller'];
  isBuyer: boolean;
};

const NegotiationHistory: React.FC<MMAOHistoryProps> = ({ chatLink, buyer, seller, events = [], isBuyer }) => {
  const { t } = useTranslation('common');
  const { user } = useUser();

  const getRequester = (event: NegotiationEvent): User => {
    if (event.createdBy === 'buyer') {
      return buyer;
    }
    if (event.createdBy === 'seller') {
      return seller;
    }
    return null;
  };

  return (
    <>
      {events.length ? (
        <div
          className={styles.negoHistory}
          data-cy="negotiation-history"
        >
          <div className={styles.negoHistory__title}>{t('MMAO.OFFER_HISTORY.TITLE')}</div>
          <ul data-cy="mmao_nego_history">
            {events.map((event: NegotiationEvent) => (
              <React.Fragment key={event.id}>
                {event.userMessage && (
                  <li
                    key={`pic_${event.id}`}
                    className={styles.negoHistory__entry}
                  >
                    <div className={styles.negoHistory__entry__userInfo}>
                      <div className={styles.negoHistory__entry__userPicture}>
                        <ProfilePicture
                          picturePath={
                            getRequester(event)?.picture?.path
                              ? `/profil/${getRequester(event).picture.path}`
                              : undefined
                          }
                          userId={getRequester(event)?.id}
                          isUserProfile={user?.id === getRequester(event)?.id}
                          width={40}
                          height={40}
                          alt={`profil-${getRequester(event)?.firstname}`}
                          wrapperProps={{ className: styles.negoHistory__entry__userPicture__circle }}
                        />
                      </div>
                      <div>
                        <p className={styles.negoHistory__entry__name}>{getRequester(event).firstname}</p>
                        <p className={styles.negoHistory__entry__date}>
                          {formatDateTime(new Date(event.creationDate), user.countryISO)}
                        </p>
                      </div>
                    </div>
                    <div className={styles.negoHistory__entry__offer}>
                      <p
                        className={styles.negoHistory__entry__offer__text}
                        data-cy="mmao_nego_history_offer_info"
                      >
                        <span className={styles.negoHistory__entry__offer__text__block}>
                          <PriceBreakDownTooltip
                            isSeller={!isBuyer}
                            offerDetails={event.offerDetails}
                            direction="left"
                          >
                            {event.userMessage}
                          </PriceBreakDownTooltip>
                        </span>
                        <span
                          className={styles.negoHistory__entry__offer__text__block}
                          data-cy="mmao_nego_history_offer_price"
                        >
                          {event.price.formatted}
                        </span>
                      </p>
                    </div>
                  </li>
                )}
              </React.Fragment>
            ))}
          </ul>
          <div className={styles.negoHistory__messageBuyer}>
            <Link
              className={styles.negoHistory__messageBuyer__button}
              href={chatLink}
            >
              <Icon
                name="chat-default"
                ariaHidden
              />
              <span className={`vc-text-H14 ${styles.negoHistory__messageBuyer__button__text}`}>
                {isBuyer ? t('MMAO.OFFER_HISTORY.MESSAGE_SELLER') : t('MMAO.OFFER_HISTORY.MESSAGE_BUYER')}
              </span>
            </Link>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default NegotiationHistory;
